import { useEffect, useMemo, useRef, useState } from 'react';
import { AnimatePresence, motion, useReducedMotion } from 'framer-motion';
import { QRCodeSVG } from 'qrcode.react';
import { Volume2, VolumeX } from 'lucide-react';
import type { PublicView } from '@ringmaster/shared';
import { Button } from '../ui/index.js';
import { preloadRoleCardArt } from '../hand/RoleRevealScreen.js';
import { SceneStack } from '../fx/SceneStack.js';
import { scenesForGame } from '../assets/scenes/index.js';
import { BurstLayer, burst, ParticleLayer, useDeviceTier } from '../fx/index.js';
import { PlayerBust, Plaque } from './components/index.js';
import { Glyph } from '../ui/Glyph.js';
import { GameTile } from '../ui/GameTile.js';
import { HowToPlayModal } from '../ui/HowToPlay.js';
import { GameSelectScreen } from '../ui/GameSelectScreen.js';
import { BackPill } from '../ui/BackPill.js';
import { RoleGalleryOverlay } from '../ui/RoleGallery.js';
import { gameById } from '../assets/manifest.js';
import { useRoom } from '../net/useRoom.js';

const OPEN_PICKER_KEY = 'rm-open-game-picker';
const howToSeenKey = (gameId: string) => `rm-howto-seen:${gameId}`;

interface LobbyScreenProps {
  view: PublicView;
  /** Host-only controls. Omitted on a read-only spectator surface (isHost=false). */
  onStart?: () => void;
  onSetConfig?: (secs: number) => void;
  isHost: boolean;
  /** Whether this host device is set to narrate only (not dealt into the game). */
  narratorOnly?: boolean;
  onSetNarratorOnly?: (narratorOnly: boolean) => void;
  /** The host's own player id — hides the kick button on their own roster row. */
  myId?: string;
  /** Host-only: remove a player from the lobby. */
  onKick?: (playerId: string) => void;
  /** Lobby music mute state + toggle (host is the sole audio surface). */
  muted?: boolean;
  onToggleMute?: () => void;
}

/**
 * Stage LobbyScreen — the pre-game marquee. A lantern-lit tent: the Blood Moon
 * wordmark up top, a carved room-code plaque + QR "come in" sign, and a "Tonight's
 * Cast" playbill where each player BUSTS IN as they join (a spark on arrival). Host
 * controls (discussion length, narrator-only, start, kick, mute) sit on the playbill;
 * the "the show can begin" line + primary button carry the full-cast state.
 *
 * Responsive: a two-column marquee on desktop/TV, a single stacked column on
 * phones (the host may run the lobby from a handset). Preserves the exact Host.tsx
 * prop contract so the join/start flow is untouched.
 */
/**
 * Scale a marquee stack DOWN to fit its container height (never up past 1×). The
 * lobby composition (wordmark + two-column playbill) is a fixed stack; on short
 * desktop viewports (e.g. 1280×720) it would otherwise overflow the fold. Rather
 * than clip or scroll, we uniformly shrink it to fit — the "compresses/scales to
 * available height" behavior. Re-measures on resize and on content changes (the
 * cast grows as players join).
 */
function useFitScale(deps: unknown[]) {
  const containerRef = useRef<HTMLDivElement>(null);
  const stackRef = useRef<HTMLDivElement>(null);
  const [scale, setScale] = useState(1);

  useEffect(() => {
    const measure = () => {
      const container = containerRef.current;
      const stack = stackRef.current;
      if (!container || !stack) return;
      // Available height inside the container's vertical padding.
      const cs = getComputedStyle(container);
      const padY = parseFloat(cs.paddingTop) + parseFloat(cs.paddingBottom);
      const avail = container.clientHeight - padY;
      // Natural (unscaled) stack height. scrollHeight is transform-independent.
      const natural = stack.scrollHeight;
      const next = natural > avail && avail > 0 ? Math.max(0.5, avail / natural) : 1;
      setScale(next);
    };
    measure();
    const ro = new ResizeObserver(measure);
    if (containerRef.current) ro.observe(containerRef.current);
    if (stackRef.current) ro.observe(stackRef.current);
    window.addEventListener('resize', measure);
    return () => {
      ro.disconnect();
      window.removeEventListener('resize', measure);
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, deps);

  return { containerRef, stackRef, scale } as const;
}

export function LobbyScreen({
  view,
  onStart,
  onSetConfig,
  isHost,
  narratorOnly = false,
  onSetNarratorOnly,
  myId,
  onKick,
  muted,
  onToggleMute,
}: LobbyScreenProps) {
  const reduced = useReducedMotion();
  const { tier } = useDeviceTier();
  const isPhone = tier === 'phone';
  const [discussMins, setDiscussMins] = useState<number>(
    Math.round(view.config.discussionSecs / 60),
  );

  const gameId = view.gameId ?? 'bloodmoon';
  const isWhisper = gameId === 'whisper';
  const game = gameById(gameId) ?? gameById('bloodmoon');
  const send = useRoom((s) => s.send);
  const [howToOpen, setHowToOpen] = useState(false);
  const [pickerOpen, setPickerOpen] = useState(false);
  // The deck, readable by anyone watching the stage while the room fills.
  const [rolesOpen, setRolesOpen] = useState(false);

  useEffect(() => {
    preloadRoleCardArt();
  }, []);

  // Host-only: auto-open the game picker once, when the END screen's
  // "Change game" button armed it before dispatching PLAY_AGAIN.
  useEffect(() => {
    if (!isHost) return;
    if (sessionStorage.getItem(OPEN_PICKER_KEY) === '1') {
      sessionStorage.removeItem(OPEN_PICKER_KEY);
      setPickerOpen(true);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isHost]);

  // Auto-show "How to play" once per game, the first time this stage reaches
  // this lobby for it.
  useEffect(() => {
    const key = howToSeenKey(gameId);
    if (!localStorage.getItem(key)) {
      setHowToOpen(true);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [gameId]);

  function dismissHowTo() {
    localStorage.setItem(howToSeenKey(gameId), '1');
    setHowToOpen(false);
  }

  const players = view.players;
  const playerCount = players.length;
  const canStart = playerCount >= view.minPlayers;
  const playUrl =
    typeof window !== 'undefined'
      ? `${window.location.origin}/play?code=${view.code}`
      : `/play?code=${view.code}`;

  const manifest = useMemo(() => scenesForGame(gameId), [gameId]);

  // Scale the marquee down to fit short viewports (no clip, no scroll).
  const { containerRef, stackRef, scale } = useFitScale([playerCount, isPhone, isHost]);

  // Spark a burst whenever a NEW player joins (count increases).
  const prevCount = useRef(playerCount);
  useEffect(() => {
    if (playerCount > prevCount.current && !reduced) {
      // Fire near the top-center where the newest bust lands.
      burst({ preset: 'sparkBurst', x: 0.5, y: 0.42, count: 14 });
    }
    prevCount.current = playerCount;
  }, [playerCount, reduced]);

  function handleMinsChange(mins: number) {
    setDiscussMins(mins);
    onSetConfig?.(mins * 60);
  }

  const scene = (
    <>
      <ParticleLayer preset={isWhisper ? 'arcanaWisps' : 'goldDust'} intensity={0.35} slot={2} />
    </>
  );

  const content = (
    <div
      ref={containerRef}
      style={{
        position: 'relative',
        zIndex: 1,
        // FIT, don't scroll: the whole marquee (wordmark + two-column playbill)
        // is a single vertically-centered stack that lives inside one viewport.
        // `height` (not `minHeight`) + centering means a tall composition centers
        // instead of anchoring to the top and spilling past the fold; `overflow`
        // hidden guarantees no scrollbar on /host at desktop sizes. On short
        // viewports the stack is uniformly scaled down (useFitScale) to fit.
        height: '100dvh',
        overflow: 'hidden',
        display: 'flex',
        flexDirection: 'column',
        justifyContent: 'center',
        alignItems: 'center',
        padding: isPhone ? '24px 20px' : '32px 24px',
      }}
    >
      <style>{`
        .rm-lobby { width: 100%; max-width: 420px; margin: 0 auto; }
        .rm-lobby-grid { display: grid; grid-template-columns: 1fr; gap: 28px; align-items: start; }
        .rm-lobby-col { display: flex; flex-direction: column; gap: 22px; min-width: 0; }
        @media (min-width: 880px) {
          .rm-lobby { max-width: 940px; }
          .rm-lobby-grid { grid-template-columns: 1fr 1fr; gap: 56px; align-items: center; }
        }
      `}</style>

      {/* Host-only "Change game" — top-left back pill (replaces the old
          tile-attached text link), safe-area aware, mirroring the mute
          button's top-right placement. */}
      {isHost && (
        <BackPill
          label="Change game"
          onClick={() => setPickerOpen(true)}
          style={{
            position: 'fixed',
            top: 'max(16px, env(safe-area-inset-top))',
            left: 'max(16px, env(safe-area-inset-left))',
            zIndex: 10,
          }}
        />
      )}

      {onToggleMute && (
        <button
          type="button"
          className="rm-hover"
          onClick={onToggleMute}
          aria-label={muted ? 'Unmute lobby music' : 'Mute lobby music'}
          title={muted ? 'Unmute lobby music' : 'Mute lobby music'}
          style={{
            position: 'fixed',
            top: 'max(16px, env(safe-area-inset-top))',
            right: 'max(16px, env(safe-area-inset-right))',
            zIndex: 10,
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            width: 44,
            height: 44,
            borderRadius: 'var(--radius-pill)',
            background: 'rgba(11,7,9,0.6)',
            border: `1px solid ${isWhisper ? 'var(--arcana-3, #8b5cf6)' : 'var(--gold-3, #a9762f)'}`,
            color: muted ? 'var(--text-tertiary)' : isWhisper ? 'var(--arcana-4, #c9b6ff)' : 'var(--gold-5, #e6b866)',
            cursor: 'pointer',
          }}
        >
          {muted ? <VolumeX size={20} strokeWidth={2} /> : <Volume2 size={20} strokeWidth={2} />}
        </button>
      )}

      {/* Scalable marquee stack — shrinks uniformly to fit short viewports. */}
      <div
        ref={stackRef}
        style={{
          display: 'flex',
          flexDirection: 'column',
          alignItems: 'center',
          gap: isPhone ? 24 : 32,
          width: '100%',
          transform: scale < 1 ? `scale(${scale})` : undefined,
          transformOrigin: 'center center',
        }}
      >
      {/* Wordmark — the flex column centers the whole marquee, so no top margin. */}
      {game?.wordmark && (
        <img
          src={game.wordmark}
          alt={game.name}
          style={{
            width: isPhone ? '62%' : '340px',
            maxWidth: 340,
            display: 'block',
            flexShrink: 0,
            filter: isWhisper
              ? 'drop-shadow(0 4px 24px rgba(139,92,246,0.5))'
              : 'drop-shadow(0 4px 24px rgba(153,0,0,0.5))',
            position: 'relative',
            zIndex: 2,
          }}
        />
      )}

      <div className="rm-lobby">
        <div className="rm-lobby-grid">
          {/* ── Left: room-code plaque + QR sign ─────────────────────────── */}
          <div className="rm-lobby-col">
            <RoomCodePlaque code={view.code} />
            <div style={{ display: 'flex', flexDirection: 'column', alignItems: 'center', gap: 10 }}>
              <div
                style={{
                  padding: 14,
                  background: isWhisper ? '#e5d9ff' : '#f7e3b0',
                  border: `3px solid ${isWhisper ? 'var(--arcana-3, #8b5cf6)' : 'var(--gold-3, #a9762f)'}`,
                  borderRadius: 16,
                  display: 'inline-block',
                  boxShadow: isWhisper
                    ? '0 0 24px rgba(139,92,246,0.5)'
                    : 'var(--rim-gold, 0 0 24px rgba(230,184,102,.5))',
                }}
              >
                <QRCodeSVG
                  value={playUrl}
                  size={isPhone ? 120 : 150}
                  level="M"
                  fgColor={isWhisper ? '#1e0f34' : '#1c0004'}
                  bgColor={isWhisper ? '#e5d9ff' : '#f7e3b0'}
                />
              </div>
              <p
                style={{
                  fontFamily: 'var(--font-ui)',
                  fontSize: 13,
                  color: isWhisper ? 'var(--arcana-4, #c9b6ff)' : 'var(--gold-5, #e6b866)',
                  margin: 0,
                  letterSpacing: '0.04em',
                }}
              >
                Scan to take your seat
              </p>
            </div>
          </div>

          {/* ── Right: Tonight's Cast playbill + controls ────────────────── */}
          <div className="rm-lobby-col">
            {/* Selected-game tile — visible to everyone, changes live when the
                host switches games. "Change game" is now the top-left back
                pill (rendered at the top of this component), not a
                tile-attached link. */}
            {game && (
              <div style={{ display: 'flex', flexDirection: 'column', gap: 8 }}>
                <GameTile
                  game={game}
                  actions={[
                    { label: 'How to play', onClick: () => setHowToOpen(true) },
                    { label: 'View roles', onClick: () => setRolesOpen(true) },
                  ]}
                />
              </div>
            )}

            <div
              style={{
                background: 'rgba(11,7,9,0.72)',
                border: `1px solid ${isWhisper ? 'var(--arcana-2, #5b3fa8)' : 'var(--gold-3, #a9762f)'}`,
                borderRadius: 'var(--radius-card, 24px)',
                padding: isPhone ? '20px 16px' : '24px 22px',
                display: 'flex',
                flexDirection: 'column',
                gap: 18,
              }}
            >
              <div
                style={{
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  gap: 10,
                  fontFamily: 'var(--font-display, serif)',
                  fontSize: isPhone ? 22 : 28,
                  color: isWhisper ? 'var(--arcana-4, #c9b6ff)' : 'var(--gold-6, #f7e3b0)',
                  borderBottom: `1px solid ${isWhisper ? 'var(--arcana-1, #2c1650)' : 'var(--gold-2, #7a4c1e)'}`,
                  paddingBottom: 12,
                }}
              >
                <Glyph name="topHat" tone={isWhisper ? 'arcana' : 'gold'} size={26} /> Tonight&rsquo;s Cast
              </div>

              {/* Cast busts — stamp in as players join. */}
              <div
                style={{
                  display: 'flex',
                  flexWrap: 'wrap',
                  gap: 14,
                  justifyContent: 'center',
                  minHeight: 96,
                  alignItems: 'flex-start',
                }}
              >
                <AnimatePresence mode="popLayout">
                  {players.map((p) => (
                    <motion.div
                      key={p.id}
                      layout
                      initial={reduced ? { opacity: 0 } : { opacity: 0, scale: 1.4, filter: 'blur(6px)' }}
                      animate={{ opacity: 1, scale: 1, filter: 'blur(0px)' }}
                      exit={reduced ? { opacity: 0 } : { opacity: 0, scale: 0.7 }}
                      transition={{ duration: reduced ? 0 : 0.4, ease: [0.7, 0, 0.16, 1] }}
                    >
                      <div style={{ position: 'relative' }}>
                        <PlayerBust
                          player={{ id: p.id, name: p.name, alive: true, avatarUrl: p.avatarUrl, discordUserId: p.discordUserId }}
                          size={isPhone ? 64 : 80}
                          state="normal"
                        />
                        {isHost && onKick && p.id !== myId && (
                          <button
                            type="button"
                            className="rm-hover"
                            onClick={() => onKick(p.id)}
                            aria-label={`Remove ${p.name}`}
                            style={{
                              position: 'absolute',
                              top: -4,
                              right: -4,
                              width: 22,
                              height: 22,
                              borderRadius: '50%',
                              border: '1px solid var(--blood-4, #990000)',
                              background: 'var(--ink-1, #0b0709)',
                              color: 'var(--blood-6, #ff5a4f)',
                              fontSize: 12,
                              lineHeight: 1,
                              cursor: 'pointer',
                            }}
                          >
                            ×
                          </button>
                        )}
                      </div>
                    </motion.div>
                  ))}
                </AnimatePresence>
                {playerCount === 0 && (
                  <p style={{ color: 'var(--text-tertiary)', fontSize: 14, fontFamily: 'var(--font-ui)', margin: '32px 0' }}>
                    Waiting for the cast to arrive…
                  </p>
                )}
              </div>

              <p
                style={{
                  textAlign: 'center',
                  fontFamily: 'var(--font-ui)',
                  fontSize: 13,
                  color: canStart
                    ? isWhisper
                      ? 'var(--arcana-4, #c9b6ff)'
                      : 'var(--gold-5, #e6b866)'
                    : 'var(--text-tertiary)',
                  margin: 0,
                }}
              >
                {canStart
                  ? `${playerCount} seated — the show can begin`
                  : `${playerCount} / ${view.minPlayers} — need ${view.minPlayers - playerCount} more`}
              </p>

              {/* Discussion length */}
              {isHost && (
                <div>
                  <p style={{ fontFamily: 'var(--font-ui)', fontSize: 13, color: 'var(--text-secondary)', margin: '0 0 8px' }}>
                    Discussion length
                  </p>
                  <div style={{ display: 'flex', gap: 8 }}>
                    {[1, 2, 3, 4, 5].map((mins) => (
                      <button
                        key={mins}
                        type="button"
                        className={discussMins === mins ? undefined : 'rm-hover-row'}
                        onClick={() => handleMinsChange(mins)}
                        style={{
                          fontFamily: 'var(--font-ui)',
                          fontSize: 14,
                          fontWeight: 600,
                          flex: 1,
                          height: 44,
                          borderRadius: 10,
                          border: `1px solid ${
                            discussMins === mins
                              ? isWhisper
                                ? 'var(--arcana-3, #8b5cf6)'
                                : 'var(--gold-4, #c9923f)'
                              : 'var(--surface-stroke)'
                          }`,
                          background:
                            discussMins === mins
                              ? isWhisper
                                ? 'rgba(139,92,246,0.18)'
                                : 'rgba(201,146,63,0.18)'
                              : 'transparent',
                          color:
                            discussMins === mins
                              ? isWhisper
                                ? 'var(--arcana-4, #c9b6ff)'
                                : 'var(--gold-6, #f7e3b0)'
                              : 'var(--text-secondary)',
                          cursor: 'pointer',
                        }}
                      >
                        {mins}m
                      </button>
                    ))}
                  </div>
                </div>
              )}

              {/* Narrator-only toggle */}
              {isHost && (
                <button
                  type="button"
                  className={narratorOnly ? undefined : 'rm-hover-row'}
                  role="switch"
                  aria-checked={narratorOnly}
                  onClick={() => onSetNarratorOnly?.(!narratorOnly)}
                  style={{
                    width: '100%',
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'space-between',
                    gap: 12,
                    padding: '12px 14px',
                    borderRadius: 10,
                    border: `1px solid ${
                      narratorOnly
                        ? isWhisper
                          ? 'var(--arcana-3, #8b5cf6)'
                          : 'var(--gold-4, #c9923f)'
                        : 'var(--surface-stroke)'
                    }`,
                    background: narratorOnly
                      ? isWhisper
                        ? 'rgba(139,92,246,0.14)'
                        : 'rgba(201,146,63,0.14)'
                      : 'transparent',
                    cursor: 'pointer',
                    textAlign: 'left',
                  }}
                >
                  <span style={{ display: 'flex', flexDirection: 'column', gap: 3 }}>
                    <span style={{ fontFamily: 'var(--font-ui)', fontSize: 14, fontWeight: 600, color: 'var(--text-primary)' }}>
                      Narrator only
                    </span>
                    <span style={{ fontFamily: 'var(--font-ui)', fontSize: 12, color: 'var(--text-tertiary)', lineHeight: 1.4 }}>
                      Run the show on this screen — don&rsquo;t deal me a role.
                    </span>
                  </span>
                  <span
                    aria-hidden
                    style={{
                      position: 'relative',
                      flexShrink: 0,
                      width: 44,
                      height: 26,
                      borderRadius: 999,
                      background: narratorOnly
                        ? isWhisper
                          ? 'var(--arcana-3, #8b5cf6)'
                          : 'var(--gold-4, #c9923f)'
                        : 'rgba(255,255,255,0.18)',
                    }}
                  >
                    <span
                      style={{
                        position: 'absolute',
                        top: 3,
                        left: narratorOnly ? 21 : 3,
                        width: 20,
                        height: 20,
                        borderRadius: '50%',
                        background: '#fff',
                        transition: 'left var(--duration-fast) var(--ease-standard)',
                      }}
                    />
                  </span>
                </button>
              )}

              {/* Start */}
              {isHost && (
                <div>
                  <Button variant="primary" onClick={() => onStart?.()} disabled={!canStart}>
                    Raise the curtain
                  </Button>
                  {narratorOnly && (
                    <p style={{ fontFamily: 'var(--font-ui)', fontSize: 12, color: 'var(--text-tertiary)', textAlign: 'center', margin: '10px 0 0' }}>
                      You&rsquo;re the narrator — {view.minPlayers}+ other players must join.
                    </p>
                  )}
                </div>
              )}
            </div>
          </div>
        </div>
      </div>
      </div>{/* /scalable marquee stack */}

      {howToOpen && <HowToPlayModal gameId={gameId} onClose={dismissHowTo} />}
      {rolesOpen && (
        <RoleGalleryOverlay
          gameId={gameId}
          playerCount={playerCount}
          onClose={() => setRolesOpen(false)}
        />
      )}
      {isHost && pickerOpen && (
        <GameSelectScreen
          currentGameId={gameId}
          onPick={(id) => send({ type: 'SWITCH_GAME', gameId: id })}
          onClose={() => setPickerOpen(false)}
        />
      )}
    </div>
  );

  return (
    <div style={{ position: 'relative', height: '100dvh', overflow: 'hidden' }}>
      <BurstLayer />
      {manifest ? (
        <SceneStack manifest={manifest} phase="LOBBY">
          {scene}
          {content}
        </SceneStack>
      ) : (
        content
      )}
    </div>
  );
}

/**
 * The carved room-code plaque — the code set in Inter (operational text) on a plate
 * that HUGS the 4 letters. The shared `Plaque` supplies the wood texture (cropped,
 * not stretched) + a carved gold frame, so a short code sits in a snug plate rather
 * than a mis-fit square.
 */
function RoomCodePlaque({ code }: { code: string }) {
  return (
    <div style={{ display: 'flex', flexDirection: 'column', alignItems: 'center', gap: 8 }}>
      <p
        style={{
          fontFamily: 'var(--font-ui)',
          fontSize: 13,
          color: 'var(--gold-5, #e6b866)',
          margin: 0,
          letterSpacing: '0.16em',
          // letter-spacing adds tracking after each glyph; pad the left the same so
          // the first letter clears the box origin and the run stays optically centered.
          paddingLeft: '0.16em',
          textTransform: 'uppercase',
          textAlign: 'center',
          whiteSpace: 'nowrap',
          // Legibility over a busy scene (curtains, lantern flames behind the HUD).
          textShadow: '0 1px 4px rgba(0,0,0,0.85), 0 0 2px rgba(0,0,0,0.9)',
        }}
      >
        Room code
      </p>
      <Plaque minInlineSize={240} padBlock={22} padInline={40} radius={16}>
        <span
          style={{
            // Room code is operational text → Inter, per the type rules.
            fontFamily: 'var(--font-ui)',
            fontSize: 56,
            fontWeight: 700,
            lineHeight: 1,
            letterSpacing: '0.14em',
            // Tracking adds trailing space after the last glyph; pad the left the same
            // amount so the run stays optically centered in the plate.
            paddingLeft: '0.14em',
            whiteSpace: 'nowrap',
            background: 'linear-gradient(180deg, var(--gold-6,#f7e3b0), var(--gold-3,#a9762f))',
            WebkitBackgroundClip: 'text',
            WebkitTextFillColor: 'transparent',
            backgroundClip: 'text',
            filter: 'drop-shadow(0 2px 8px rgba(0,0,0,0.85))',
          }}
        >
          {code}
        </span>
      </Plaque>
    </div>
  );
}
